import { fetchWithAcceptHeaders } from '../utils/flowBuilderGridFetch';
import { getStaticBaseUrl } from '../utils/publicUrl';
let cachedEnvConfig = null;
let envConfigPromise = null;
/** Synchronous read after `loadEnvConfig` has resolved at least once (e.g. NavBar preload). */
export function getCachedEnvConfig() {
    return cachedEnvConfig;
}
/**
 * `iframe` — embed auth UI in a dialog (breaks when the flow redirects to IdPs that forbid framing, e.g. OneLogin).
 * `popup` — open auth URL in a new window (required for most OIDC / OneLogin flows).
 * `redirect` — full page navigation to auth URL (typical for `env.json` AuthUILoginMode).
 */
export function readAuthUILoginEmbedMode(env) {
    if (!env)
        return 'redirect';
    const m = String(env.AuthUILoginMode ?? '').toLowerCase().trim();
    if (m === 'redirect')
        return 'redirect';
    if (m === 'popup')
        return 'popup';
    if (m === 'iframe')
        return 'iframe';
    const lp = env.LoginUsePopup;
    if (lp === true || lp === 'Y' || String(lp).toLowerCase() === 'true')
        return 'popup';
    return 'redirect';
}
/**
 * Parses `AuthorizationLogins` into labeled login URLs for the NavBar.
 * Each item needs `AuthorizationPath` (URL) and optional `Name` (button label).
 */
export function readAuthorizationLoginOptions(env) {
    if (!env)
        return [];
    const raw = env.AuthorizationLogins;
    if (!Array.isArray(raw))
        return [];
    const out = [];
    for (const item of raw) {
        if (!item || typeof item !== 'object')
            continue;
        const o = item;
        const path = o.AuthorizationPath ?? o.authorizationPath;
        const name = o.Name ?? o.name;
        const portal = o.AccessPortal ?? o.accessPortal;
        if (typeof path !== 'string' || !path.trim())
            continue;
        const label = typeof name === 'string' && name.trim().length > 0 ? name.trim() : 'Sign in';
        out.push({
            label,
            url: path.trim(),
            accessPortal: typeof portal === 'string' ? portal : undefined,
        });
    }
    return out;
}
function readAuthorizationLoginOptionByPortal(env, accessPortal) {
    const normalizedPortal = accessPortal.trim().toUpperCase();
    if (!normalizedPortal)
        return undefined;
    return readAuthorizationLoginOptions(env).find((opt) => opt.accessPortal?.trim().toUpperCase() === normalizedPortal);
}
/** Customer / external login URL (`AccessPortal = E`) with first-option fallback. */
export function readCustomerLoginUrl(env) {
    const options = readAuthorizationLoginOptions(env);
    if (options.length === 0)
        return '';
    return readAuthorizationLoginOptionByPortal(env, 'E')?.url ?? options[0].url;
}
/** Staff / internal login URL (`AccessPortal = I`). */
export function readStaffLoginUrl(env) {
    return readAuthorizationLoginOptionByPortal(env, 'I')?.url ?? '';
}
/** Sign-up / registration URL from `env.json` (`UserRegistrationUrl`). */
export function readUserRegistrationUrl(env) {
    if (!env)
        return '';
    const u = env.UserRegistrationUrl ?? env.userRegistrationUrl;
    return typeof u === 'string' && u.trim().length > 0 ? u.trim() : '';
}
/** Sign-up / registration URL from `env.json` (`UserRegistrationUrl`). */
export function readStaffRegistrationUrl(env) {
    if (!env)
        return '';
    const u = env.StaffRegistrationUrl ?? env.staffRegistrationUrl;
    return typeof u === 'string' && u.trim().length > 0 ? u.trim() : '';
}
/** Internal (staff) portal URL: explicit `InternalURL`, else **`Base` + `InternalBasepath`**. */
export function readInternalPortalUrl(env) {
    if (!env)
        return '';
    const u = env.InternalURL ?? env.internalURL ?? env.internalUrl;
    if (typeof u === 'string' && u.trim().length > 0)
        return u.trim();
    const base = typeof env.Base === 'string' ? env.Base.trim().replace(/\/$/, '') : '';
    const internal = env.InternalBasepath ?? env.internalBasepath;
    if (base && typeof internal === 'string' && internal.startsWith('/')) {
        return `${base}${internal}`;
    }
    return '';
}
/** Logout URL from `env.json` (`LogOutUrl`). */
export function readLogoutUrl(env) {
    if (!env)
        return '';
    const u = env.LogOutUrl ?? env.logoutUrl ?? env.logouturl;
    return typeof u === 'string' && u.trim().length > 0 ? u.trim() : '';
}
/** OneLogin (or other IdP) user profile URL from `env.json` (`ProfileUrl`) — shown in the NavBar profile menu. */
export function readProfileUrl(env) {
    if (!env)
        return '';
    const u = env.ProfileUrl ?? env.profileUrl;
    return typeof u === 'string' && u.trim().length > 0 ? u.trim() : '';
}
/**
 * URL of the Delasoft accessibility widget's Module Federation entry (`env.json`
 * `AccessibilityRemoteUrl`) — e.g. `https://cdn.example.com/a11y/remoteEntry.js`,
 * or a path relative to this app's base for a copy served from `public/`.
 * Empty/unset means the widget is not mounted (see `a11y/delasoftAccessibility.ts`).
 */
export function readAccessibilityRemoteUrl(env) {
    if (!env)
        return '';
    const u = env.AccessibilityRemoteUrl ?? env.accessibilityRemoteUrl;
    return typeof u === 'string' && u.trim().length > 0 ? u.trim() : '';
}
/** Default auth URL for permit “view details” / iframe login: customer (`E`) first, else first listed, else legacy keys. */
export function readDefaultAuthUserUrl(env) {
    const customerUrl = readCustomerLoginUrl(env);
    if (customerUrl)
        return customerUrl;
    const legacy = env && typeof env.AuthUserUI === 'string' && env.AuthUserUI.trim()
        ? env.AuthUserUI.trim()
        : env && typeof env.AuthUserUrl === 'string' && env.AuthUserUrl.trim()
            ? env.AuthUserUrl.trim()
            : env && typeof env.AuthorizationUserUrl === 'string' && env.AuthorizationUserUrl.trim()
                ? env.AuthorizationUserUrl.trim()
                : '';
    return legacy;
}
/** Sidebar base URL (`env.json` `Sidebar`) — same host as Flow Builder grid APIs. */
export function readSidebarBase(env) {
    if (!env)
        return '';
    const s = env.Sidebar ?? env.sidebar;
    return typeof s === 'string' && s.trim().length > 0 ? s.trim().replace(/\/$/, '') : '';
}
/**
 * Origin used to build `#/ProcessFlows/{flowId}/{version}/{recordId}` claim URLs when `ClaimMyRecordUrl` is unset.
 * Prefers explicit keys, then `ProcessFlowDetailsUrl` / `PreviewURL1` / `PreviewURL` host.
 */
export function readProcessFlowViewerOrigin(env) {
    if (!env)
        return '';
    const explicit = [
        env.FlowViewerOrigin,
        env.flowViewerOrigin,
        env.ProcessFlowViewerOrigin,
        env.processFlowViewerOrigin,
        env.ClaimMyRecordOrigin,
        env.claimMyRecordOrigin,
    ];
    for (const c of explicit) {
        if (typeof c === 'string' && /^https?:\/\//i.test(c.trim())) {
            try {
                return new URL(c.trim()).origin;
            }
            catch {
                /* continue */
            }
        }
    }
    const detail = env.ProcessFlowDetailsUrl ?? env.processFlowDetailsUrl;
    if (typeof detail === 'string' && detail.includes('#')) {
        const base = detail.split('#')[0].trim().replace(/\/+$/, '');
        if (/^https?:\/\//i.test(base))
            return base;
    }
    for (const key of ['PreviewURL1', 'previewURL1', 'PreviewURL', 'previewURL']) {
        const p = env[key];
        if (typeof p === 'string' && p.includes('#')) {
            const base = p.split('#')[0].trim().replace(/\/+$/, '');
            if (/^https?:\/\//i.test(base))
                return base;
        }
    }
    return '';
}
/**
 * Citizen / logged-in “Dashboard” first menu item (`resolveDashboardNav`).
 *
 * Order: explicit `CitizenDashboardUrl` (or `citizenDashboardUrl`), else **`Base` + `ExternalBasepath`**
 * (typically `…/ONESTOP-UI-External/#/`). If you see the External app instead of Flow Viewer or another shell,
 * set `CitizenDashboardUrl` in `env.json` to the URL you want (e.g. Flow Viewer `http://localhost:4001/#/`).
 */
export function readCitizenDashboardUrl(env) {
    if (!env)
        return '';
    const u = env.CitizenDashboardUrl ?? env.citizenDashboardUrl;
    if (typeof u === 'string' && u.trim().length > 0)
        return u.trim();
    const base = typeof env.Base === 'string' ? env.Base.trim().replace(/\/$/, '') : '';
    const ext = env.ExternalBasepath ?? env.externalBasepath;
    if (base && typeof ext === 'string' && ext.startsWith('/')) {
        return `${base}${ext}`;
    }
    return '';
}
/**
 * App URLs the portal frames (SafeAeon v1 EXT-002): the Flow Viewer, citizen dashboard, preview and claim hosts
 * configured here. Their origins are allowed alongside env.json `EmbedAllowedOrigins`, which the library reads
 * (`components/library/Utils/embedAllowedOrigins.ts`).
 */
export function readEmbedConfiguredUrls(env) {
    if (!env)
        return [];
    return [
        readProcessFlowViewerOrigin(env),
        readCitizenDashboardUrl(env),
        env.ProcessFlowDetailsUrl,
        env.processFlowDetailsUrl,
        env.PermitViewDetailsUrl,
        env.ClaimMyRecordUrl,
        env.ProcessPreviewURL,
        env.Processpreviewurl,
        env.processPreviewURL,
        env.processPreviewUrl,
        env.PreviewURL,
        env.PreviewURL1,
    ];
}
/**
 * Pulls flow semver from `#/ProcessFlows/{flowId}/{version}/...` (e.g. `ProcessFlowDetailsUrl` in `env.json`).
 */
export function parseProcessFlowVersionFromDetailsUrl(url) {
    if (!url || typeof url !== 'string')
        return undefined;
    const marker = '#/ProcessFlows/';
    const i = url.indexOf(marker);
    if (i < 0)
        return undefined;
    const rest = url.slice(i + marker.length);
    const parts = rest.split('/').filter(Boolean);
    if (parts.length >= 2 && parts[1].trim())
        return parts[1].trim();
    return undefined;
}
/**
 * Default Flow Builder semver for URLs when a row/env omits an explicit version.
 * Order: `DefaultProcessFlowVersion` in `public/env.json` → version segment of `ProcessFlowDetailsUrl` → `'1.1'`.
 */
export function readDefaultProcessFlowVersion(env) {
    if (!env)
        return '1.1';
    const explicit = env.DefaultProcessFlowVersion ??
        env.defaultProcessFlowVersion ??
        env.ProcessFlowDefaultVersion ??
        env.processFlowDefaultVersion;
    if (typeof explicit === 'string' && explicit.trim())
        return explicit.trim();
    const detail = typeof env.ProcessFlowDetailsUrl === 'string'
        ? env.ProcessFlowDetailsUrl
        : typeof env.processFlowDetailsUrl === 'string'
            ? env.processFlowDetailsUrl
            : undefined;
    const parsed = parseProcessFlowVersionFromDetailsUrl(detail);
    if (parsed)
        return parsed;
    return '1.1';
}
/**
 * “Claim my record” flow entry (`#/ProcessFlows/{id}/{ver}/{recordId}`).
 * Set `ClaimMyRecordUrl` for a full override; otherwise builds from `readProcessFlowViewerOrigin` + optional ids.
 */
export function readClaimMyRecordUrl(env) {
    if (!env)
        return '';
    const direct = env.ClaimMyRecordUrl ?? env.claimMyRecordUrl;
    if (typeof direct === 'string' && direct.trim().length > 0)
        return direct.trim();
    const flowId = typeof env.ClaimMyRecordFlowId === 'string' && env.ClaimMyRecordFlowId.trim()
        ? env.ClaimMyRecordFlowId.trim()
        : '17UF18ZWGBNUFT05R6OZ9CSEE';
    const ver = typeof env.ClaimMyRecordFlowVersion === 'string' && env.ClaimMyRecordFlowVersion.trim()
        ? env.ClaimMyRecordFlowVersion.trim()
        : readDefaultProcessFlowVersion(env);
    const recordId = typeof env.ClaimMyRecordRecordId === 'string' && env.ClaimMyRecordRecordId.trim()
        ? env.ClaimMyRecordRecordId.trim()
        : '0';
    const origin = readProcessFlowViewerOrigin(env);
    if (!origin)
        return '';
    return `${origin.replace(/\/$/, '')}#/ProcessFlows/${flowId}/${ver}/${recordId}`;
}
/**
 * Grid id for `/permits/:departmentId` agency list (`FlowBuilderRecord/Grid/{id}?filter=DEPARTMENT~contains~'{departmentId}'`).
 */
export function readDepartmentAgenciesGridId(env) {
    if (!env)
        return '';
    const id = env.DepartmentAgenciesGridId ?? env.departmentAgenciesGridId;
    return typeof id === 'string' && id.trim().length > 0 ? id.trim() : '';
}
/**
 * Optional full permits / catalog search URL for the landing hero (e.g. external Flow/Solr UI).
 * Use `{q}` for encoded query, or omit it to append `?q=` / `&q=`. When unset, only in-memory matches are shown.
 */
export function readLandingPermitCatalogSearchUrl(env) {
    if (!env)
        return '';
    const u = env.LandingPermitCatalogSearchUrl ??
        env.landingPermitCatalogSearchUrl ??
        env.PermitSearchUrl ??
        env.permitSearchUrl;
    return typeof u === 'string' && u.trim().length > 0 ? u.trim() : '';
}
/**
 * Grid id for landing page agency list (`PublicFlowBuilderRecord/Grid/{id}`).
 * Falls back to `DepartmentAgenciesGridId` when unset (same Flow Builder grid).
 */
export function readLandingAgenciesGridId(env) {
    if (!env)
        return '';
    const id = env.LandingAgenciesGridId ?? env.landingAgenciesGridId;
    if (typeof id === 'string' && id.trim().length > 0)
        return id.trim();
    return readDepartmentAgenciesGridId(env);
}
/** Departments master grid (`LandingDepartmentsGridId`) — used to resolve hero title for numeric `/permits/:id` routes. */
export function readLandingDepartmentsGridId(env) {
    if (!env)
        return '';
    const id = env.LandingDepartmentsGridId ?? env.landingDepartmentsGridId;
    return typeof id === 'string' && id.trim().length > 0 ? id.trim() : '';
}
export function readDepartmentAgenciesGridPageSize(env) {
    if (!env)
        return 100;
    const n = env.DepartmentAgenciesGridPageSize ?? env.departmentAgenciesGridPageSize;
    if (typeof n === 'number' && Number.isFinite(n) && n > 0)
        return Math.min(500, Math.floor(n));
    if (typeof n === 'string' && /^\d+$/.test(n.trim()))
        return Math.min(500, parseInt(n, 10));
    return 100;
}
/** Grid id for permit rows when an agency accordion opens (`AGENCY` + `DEPARTMENT` filter). */
export function readDepartmentAgencyPermitsGridId(env) {
    if (!env)
        return '';
    const id = env.DepartmentAgencyPermitsGridId ??
        env.departmentAgencyPermitsGridId ??
        env.PermitListGridId ??
        env.permitListGridId;
    return typeof id === 'string' && id.trim().length > 0 ? id.trim() : '';
}
export function readDepartmentAgencyPermitsGridPageSize(env) {
    if (!env)
        return 25;
    const n = env.DepartmentAgencyPermitsGridPageSize ??
        env.departmentAgencyPermitsGridPageSize ??
        env.DepartmentAgencyPermitsPageSize;
    if (typeof n === 'number' && Number.isFinite(n) && n > 0)
        return Math.min(500, Math.floor(n));
    if (typeof n === 'string' && /^\d+$/.test(n.trim()))
        return Math.min(500, parseInt(n, 10));
    return 25;
}
/**
 * Page size for the unfiltered landing permits catalog (`PublicFlowBuilderRecord/Grid/{id}` with `page` / `pageSize` only).
 * Default and max **500** per request.
 */
export function readLandingPermitsCatalogPageSize(env) {
    if (!env)
        return 500;
    const n = env.LandingPermitsCatalogPageSize ?? env.landingPermitsCatalogPageSize;
    if (typeof n === 'number' && Number.isFinite(n) && n > 0)
        return Math.min(500, Math.floor(n));
    if (typeof n === 'string' && /^\d+$/.test(n.trim()))
        return Math.min(500, parseInt(n.trim(), 10));
    return 500;
}
/**
 * Grid id for bulk permit rows on the landing page. Defaults to {@link readDepartmentAgencyPermitsGridId} (same as department accordion permits).
 */
export function readLandingPermitsCatalogGridId(env) {
    if (!env)
        return '';
    const id = env.LandingPermitsCatalogGridId ?? env.landingPermitsCatalogGridId;
    if (typeof id === 'string' && id.trim().length > 0)
        return id.trim();
    return readDepartmentAgencyPermitsGridId(env);
}
/** Grid id for the Agency Contacts page (`AgencyContactsGridId`). */
export function readAgencyContactsGridId(env) {
    if (!env)
        return '';
    const id = env.AgencyContactsGridId ?? env.agencyContactsGridId;
    return typeof id === 'string' && id.trim().length > 0 ? id.trim() : '';
}
/** Page size per request for the Agency Contacts grid (`AgencyContactsGridPageSize`); default 100, max 500. */
export function readAgencyContactsGridPageSize(env) {
    if (!env)
        return 100;
    const n = env.AgencyContactsGridPageSize ?? env.agencyContactsGridPageSize;
    if (typeof n === 'number' && Number.isFinite(n) && n > 0)
        return Math.min(500, Math.floor(n));
    if (typeof n === 'string' && /^\d+$/.test(n.trim()))
        return Math.min(500, parseInt(n.trim(), 10));
    return 100;
}
/**
 * Parses `HelpVideos` into labeled YouTube links (`Name` + `Url`, case-insensitive).
 * Rows missing a usable URL are dropped; unnamed rows fall back to `"Video"`.
 */
export function readHelpVideoLinks(env) {
    if (!env)
        return [];
    const raw = env.HelpVideos ?? env.helpVideos;
    if (!Array.isArray(raw))
        return [];
    const out = [];
    for (const item of raw) {
        if (!item || typeof item !== 'object')
            continue;
        const o = item;
        const url = o.Url ?? o.url ?? o.Link ?? o.link;
        const name = o.Name ?? o.name;
        if (typeof url !== 'string' || !url.trim())
            continue;
        out.push({
            name: typeof name === 'string' && name.trim().length > 0 ? name.trim() : 'Video',
            url: url.trim(),
        });
    }
    return out;
}
export async function loadEnvConfig() {
    if (cachedEnvConfig)
        return cachedEnvConfig;
    if (envConfigPromise)
        return envConfigPromise;
    const envUrl = `${getStaticBaseUrl().replace(/\/?$/, '/')}env.json`;
    envConfigPromise = fetchWithAcceptHeaders(envUrl)
        .then(async (res) => {
        if (!res.ok)
            return null;
        const json = (await res.json());
        cachedEnvConfig = json;
        return json;
    })
        .catch(() => null)
        .finally(() => {
        envConfigPromise = null;
    });
    return envConfigPromise;
}
